import React, { memo, useRef } from "react";

import { Box, Button } from "@chakra-ui/react";
import { ReactComponent as AnalyticsIcon } from "assets/icons/analytics-icon.svg";
import { ReactComponent as BillPaymentsMultipleIcon } from "assets/icons/bill-payments-multiple.svg";
import { ReactComponent as CloseIcon } from "assets/icons/close-icon-in-grey-bg.svg";
import { handleAmountFormat, handleTruncateAmount } from "utils";
import { ONE_NAIRA_IN_KOBO, ZERO } from "utils/constants";
import { Maybe } from "utils/general/maybe";

import { DropDown } from "ui/dropdown";
import { IDropDownRef } from "ui/dropdown/types";
import { Spinner } from "ui/spinner";
import { tableHeaderCtaCss } from "ui/table/styles";
import { Heading2, Heading3, Paragraph4 } from "ui/typography";

import { TransactionsSubRouteName } from "../content";
import { ITransactionsAnalyticsProps } from "./types";

const DROP_DOWN_WIDTH = 501;
const DROP_DOWN_TOP_POSITION = 80;

const analyticsTextsMap: Record<
  TransactionsSubRouteName,
  {
    title: string;
    successRatesTitle: string;
  }
> = {
  transfers: {
    title: "Transfer Analytics",
    successRatesTitle: "Transfer Success Rate",
  },
  "bills-payments": {
    title: "Bills Payments Analytics",
    successRatesTitle: "Bills Payments Success Rate",
  },
  odp: {
    title: "On Demand Pay Analytics",
    successRatesTitle: "On Demand Pay Success Rate",
  },
};

export const TransactionsAnalytics = memo(
  (props: ITransactionsAnalyticsProps) => {
    const { analytics, isLoadingTransactionAnalytics, transactionType } = props;
    const totalAmount = analytics?.totalAmount || ZERO;
    const totalCount = analytics?.totalCount || ZERO;
    const averageAmount = analytics?.averageAmount || ZERO;
    const successPercentage = analytics?.successPercentage || ZERO;
    const failedPercentage = analytics?.failedPercentage || ZERO;
    const dropDownRef = useRef<IDropDownRef | null>(null);

    const totalAmountValue = handleAmountFormat({
      value: String(handleTruncateAmount(totalAmount) / ONE_NAIRA_IN_KOBO),
      isFormattedAmtNotPadded: true,
      shouldFormatZero: true,
      includeNairaSymbol: true,
    });
    const averageTransferAmount = handleAmountFormat({
      value: String(handleTruncateAmount(averageAmount) / ONE_NAIRA_IN_KOBO),
      isFormattedAmtNotPadded: true,
      shouldFormatZero: true,
      includeNairaSymbol: true,
    });
    const totalCountValue = handleAmountFormat({
      value: String(totalCount),
      shouldFormatZero: true,
    });
    const successRate = `${successPercentage}%`;
    const failureRate = `${failedPercentage}%`;

    const handleCloseAnalytics = () => {
      if (!dropDownRef.current) return;
      dropDownRef.current.handleToggleDropdown();
    };

    return (
      <DropDown
        dropDownWidth={DROP_DOWN_WIDTH}
        dropDownBtnContent={
          <>
            <AnalyticsIcon className="w-4 h-4" />
            <p className="text-sm text-[#374151] font-semibold">Analytics</p>
          </>
        }
        customDropDownButtonCss={tableHeaderCtaCss}
        ref={dropDownRef}
        dropDownPopUpTopPosition={DROP_DOWN_TOP_POSITION}
        dropDownPopUpMenuContentCss={{
          boxShadow: "0px 0px 7.4px 0px #0000000D",
          padding: "24px",
          borderRadius: "12px",
          gap: "32px",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box className="flex items-center justify-between h-[35px] border-b border-[#F3F4F6]">
          <Heading3
            fontWeight="700"
            textColor="#1A202C"
            lineHeight="27.32px"
          >
            {analyticsTextsMap[transactionType].title}
          </Heading3>
          <Box
            className={`${isLoadingTransactionAnalytics ? "cursor-not-allowed" : "cursor-pointer"}`}
            onClick={
              !isLoadingTransactionAnalytics ? handleCloseAnalytics : undefined
            }
          >
            <CloseIcon />
          </Box>
        </Box>
        <Maybe condition={isLoadingTransactionAnalytics}>
          <Box className="flex justify-center items-center h-[401px]">
            <Spinner size="40px" />
          </Box>
        </Maybe>
        <Maybe condition={!isLoadingTransactionAnalytics}>
          <Box className="flex flex-col gap-6">
            <Box
              className={`flex gap-4 ${transactionType === TransactionsSubRouteName.TRANSFERS ? "flex-col-reverse" : "flex-col"}`}
            >
              <Maybe
                condition={
                  transactionType === TransactionsSubRouteName.TRANSFERS
                }
              >
                <Box className="rounded-[20px] border border-[#E5E7EB] p-4 flex flex-col gap-6">
                  <Box>
                    <Paragraph4
                      lineHeight="22px"
                      textColor="#374151"
                    >
                      Total Transfer Value
                    </Paragraph4>
                    <p className="text-[35px] leading-[66px] text-[#151522]">
                      {totalAmountValue}
                    </p>
                  </Box>
                  <Box className="h-9 flex items-center gap-12">
                    <Box className="flex flex-col gap-1">
                      <p className="text-[12px] leading-4 text-[#718096]">
                        Transfer Counts
                      </p>
                      <p className="text-sm font-semibold text-[#1A202C]">
                        {totalCountValue}
                      </p>
                    </Box>
                    <Box className="flex flex-col gap-1">
                      <p className="text-[12px] leading-4 text-[#718096]">
                        Average Transfer Amount
                      </p>
                      <p className="text-sm font-semibold text-[#1A202C]">
                        {averageTransferAmount}
                      </p>
                    </Box>
                  </Box>
                </Box>
              </Maybe>
              <Maybe
                condition={
                  transactionType === TransactionsSubRouteName.BILLS_PAYMENTS
                }
              >
                <Box className="rounded-[20px] border border-[#E5E7EB] p-4 flex flex-col gap-12">
                  <Box>
                    <Paragraph4
                      lineHeight="22px"
                      textColor="#374151"
                    >
                      Total Bills Payments Value
                    </Paragraph4>
                    <Heading2
                      fontWeight="700"
                      lineHeight="23px"
                      textColor="#1A202C"
                    >
                      {totalAmountValue}
                    </Heading2>
                  </Box>
                  <Box className="h-9 flex items-center gap-12">
                    <Box className="flex flex-col gap-1">
                      <p className="text-[12px] leading-4 text-[#718096]">
                        Total Bills Payments Counts
                      </p>
                      <p className="text-sm font-semibold text-[#1A202C]">
                        {totalCountValue}
                      </p>
                    </Box>
                    <Box>
                      <BillPaymentsMultipleIcon />
                    </Box>
                  </Box>
                </Box>
              </Maybe>
              <Box className="rounded-[20px] border border-[#E5E7EB] p-4 flex flex-col gap-[6px]">
                <Paragraph4
                  lineHeight="22px"
                  textColor="#374151"
                >
                  {analyticsTextsMap[transactionType].successRatesTitle}
                </Paragraph4>
                <Box className="flex gap-[15px] items-center">
                  <Box className="flex flex-col h-[82px]">
                    <p className="text-[35px] leading-[66px] text-[#151522]">
                      {successRate}
                    </p>
                    <Box className="h-4 flex items-center gap-1">
                      <p className="text-[12px] leading-4 text-[#718096]">
                        Success
                      </p>
                      <Box className="w-[13px] h-[13px] rounded-[50%] bg-[#009642]"></Box>
                    </Box>
                  </Box>
                  <Box className="w-[25px] h-[38px] flex justify-center items-center">
                    <Box className="w-1 h-1 bg-[#9CA3AF] rounded-[50%]"></Box>
                  </Box>
                  <Box className="flex flex-col h-[82px]">
                    <p className="text-[35px] leading-[66px] text-[#151522]">
                      {failureRate}
                    </p>
                    <Box className="h-4 flex items-center gap-1">
                      <p className="text-[12px] leading-4 text-[#718096]">
                        Failure
                      </p>
                      <Box className="w-[13px] h-[13px] rounded-[50%] bg-[#EF4444]"></Box>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>
            <Box className="flex justify-end items-center">
              <Button
                className="w-[86px] h-12 rounded-lg py-[14px] px-6 flex justify-center items-center font-semibold text-[16px] leading-[20px] bg-[#00535C] text-white border border-[#00535C] disabled:cursor-not-allowed disabled:opacity-50"
                css={{
                  boxShadow: "0px 1px 2px 0px #1018280D",
                }}
                onClick={handleCloseAnalytics}
              >
                Okay
              </Button>
            </Box>
          </Box>
        </Maybe>
      </DropDown>
    );
  }
);
